import { useCallback, useMemo, useState } from 'react'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import cn from 'classnames'
import { Location } from 'history'
import Icon from '../../Common/Icon'
import Text from '../../Common/Text'
import styles from '../SideMenuClient.module.scss'
import { IMenuData, ISubMenuData } from '../SideMenuConfig'
import { isItemActive } from '../utils'
import NestedNavItem from './NestedNavItem'

interface IProps extends Partial<IMenuData> {
  handleItemExpand: (itemName: string) => void
  itemExpanded: boolean
  sidebarExpanded: boolean
  location: Location
  toggleSidebar?: () => void
}

const ExpandableNavItem = ({
  icon,
  label,
  subItems,
  handleItemExpand,
  itemExpanded,
  sidebarExpanded,
  location,
  toggleSidebar,
}: IProps) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleExpandClick = useCallback(() => handleItemExpand(label), [handleItemExpand, label])

  const isActive = useMemo(() => isItemActive(subItems, location), [location, subItems])

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  return (
    <>
      <div className={cn({ [styles.active]: isActive })}>
        <ListItemButton
          className={cn(styles.navItem, {
            [styles.navItemExpanded]: itemExpanded,
            [styles.isHovered]: isHovered,
          })}
          onClick={handleExpandClick}
          disableRipple
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className={styles.icon}>
            {icon}
            <span className={styles.expandableIcon}>
              {itemExpanded ? (
                <Icon name="chevron-up" size="sm" className={styles.dropdownIcon} />
              ) : (
                <Icon name="chevron-down" size="sm" className={styles.dropdownIcon} />
              )}
            </span>
          </div>
          {sidebarExpanded && (
            <Text
              variant="menuItem"
              tone={isActive ? 'inverse' : 'primary'}
              casing="role"
              component="div"
            >
              {label}
            </Text>
          )}
        </ListItemButton>
      </div>
      <Collapse in={itemExpanded} timeout={0} unmountOnExit>
        <List
          className={cn(styles.nestedNav, { [styles.isHovered]: isHovered })}
          component="div"
          disablePadding
        >
          {subItems.map(({ label: itemLabel, link, icon: subMenuIcon }: ISubMenuData) => (
            <NestedNavItem
              link={link}
              label={itemLabel}
              sidebarExpanded={sidebarExpanded}
              key={link}
              icon={subMenuIcon}
              toggleSidebar={toggleSidebar}
            />
          ))}
        </List>
      </Collapse>
    </>
  )
}

export default ExpandableNavItem
